// src/components/admin/ViewUsersModalForm.tsx

/*
Form to Adding/Editing/Deleting users in Each Devices
*/


import { useEffect, useState } from "react";
import { Modal, Button, Table, Spinner, Alert, Form } from "react-bootstrap";
// import authApi from "services/authApi";
import http from "services/httpClient";

interface User {
  id: number;
  email: string;
  global_role: string;
  client_role: string;
  is_active: boolean;
  date_created: string;
}

interface Props {
  clientId: number;
  show: boolean;
  onClose: () => void;
  onUserChange?: () => void;
}

function ViewUsersModalForm({ clientId, show, onClose, onUserChange }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<string>("success");
  const [isAdding, setIsAdding] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editRole, setEditRole] = useState("viewer");
  
  const fetchUsers = async () => {
    try {
        setLoading(true);
        const response = await http.get(`/clients/${clientId}/users`);
        const data = await response.data;
        setUsers(data.users || data);
    } catch (err) {
      setAlertMessage("Failed to load users.");
      setAlertVariant("danger");
    //   console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newEmail.trim()) return;

    setIsAdding(true); // ⏳ show spinner

    try {
        await http.post(`/auth/register`, {
        email: newEmail,
        role: "client",
        });
    } catch (err: any) {
        if (err.response?.status !== 409) {
        setAlertMessage("Failed to add user.");
        setAlertVariant("danger");
        setIsAdding(false); // 🛑 stop spinner
        return;
        }
    }

    try {
        await http.post(`/clients/${clientId}/users`, {
        email: newEmail,
        role,
        });

        await fetchUsers();
        onUserChange?.();
        setNewEmail("");
        setAlertMessage("User added successfully!");
        setAlertVariant("success");
    } catch (err: any) {
        setAlertMessage(
        err.response?.status === 409
            ? "User already assigned to client."
            : "Failed to assign user."
        );
        setAlertVariant("danger");
    } finally {
        setIsAdding(false); // ✅ stop spinner
        setTimeout(() => setAlertMessage(null), 3000);
    }
    };


  const handleEditUser = async (userId: number) => {
    try {
      await http.patch(`/clients/${clientId}/users/${userId}`, {
        role: editRole,
      });

      await fetchUsers();
      onUserChange?.();

      setEditingUserId(null);
      setAlertMessage("User role updated successfully!");
      setAlertVariant("success");

      setTimeout(() => setAlertMessage(null), 3000);
    } catch (err) {
      setAlertMessage("Failed to edit user.");
      setAlertVariant("danger");
    }
  };

  const startEditUser = (user: User) => {
    setEditingUserId(user.id);
    setEditRole(user.client_role);
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm("Remove user from client?")) return;
    try {
      await http.delete(`/clients/${clientId}/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      onUserChange?.();
    } catch (err) {
      setAlertMessage("Failed to delete user.");
      setAlertVariant("danger");
    //   console.error(err);
    }
  };
  

  useEffect(() => {
    if (show) fetchUsers();
  }, [show]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Client Users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex gap-2">
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="viewer">Viewer</option>
            <option value="manager">Manager</option>
          </Form.Select>
          <Button variant="primary" onClick={handleAddUser} disabled={isAdding}>
            {isAdding ? (
                <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
                </>
            ) : (
                "Add User"
            )}
            </Button>

        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <Table bordered hover responsive="sm">
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Email</th>
                <th>Global Role</th>
                <th>Client Role</th>
                <th>Createdzz</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ maxWidth: "350px", wordBreak: "break-all" }}>{u.email}</td>
                  <td>{u.global_role}</td>
                  <td>
                    {editingUserId === u.id ? (
                      <Form.Select
                        size="sm"
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                      >
                        <option value="viewer">Viewer</option>
                        <option value="manager">Manager</option>
                      </Form.Select>
                    ) : (
                      u.client_role
                    )}
                  </td>
                  <td>{new Date(u.date_created).toLocaleString()}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {editingUserId === u.id ? (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleEditUser(u.id)}
                          >
                            Save
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={cancelEditUser}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => startEditUser(u)}
                          >
                            Edit
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteUser(u.id)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div>
        {alertMessage && (
            <Alert
            variant={alertVariant}
            onClose={() => setAlertMessage(null)}
            dismissible
            >
            {alertMessage}
            </Alert>
        )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ViewUsersModalForm;
